import React, {useEffect, useState} from 'react';
import axios from 'axios';
import {
    ActionsContainer,
    EmptyGroupItem,
    GroupItem,
    GroupList,
    SearchInput,
    StyledDialog,
    StyledSelect
} from './DataSourcePermissionDialog.styled';
import {DataSourcePermissionQueryResult, GroupQueryResult} from "../../models/models";
import {StyledTable, StyledTableCell, StyledTableRow, StyledTableWrapper} from "../../pages/admin/GroupList/GroupList.styled";
import {Box, Button, InputAdornment} from "@mui/material";
import FieldHelpSuffix from '../common/FieldHelpSuffix';
import {useSnackbar} from "../../contexts/SnackbarContext";
import {ActionButton, CancelButton} from '../common/ActionButtons';
import {parseError} from "../../errors/http.service";
import {useTranslation} from 'react-i18next';
import {groupsService} from "../../services/groups.service";

interface DataSourcePermissionDialogProps {
    open: boolean;
    dataSourceId: number;
    owner: string;
    onClose: () => void;
}

const DataSourcePermissionDialog = ({
    open,
    dataSourceId,
    owner,
    onClose
}: DataSourcePermissionDialogProps) => {
    const {t} = useTranslation();
    const {showSnackbar} = useSnackbar();
    const [dataSourceOwner, setDataSourceOwner] = useState(owner);
    const [searchQuery, setSearchQuery] = useState('');
    const [searchGroupResults, setSearchGroupResults] = useState<GroupQueryResult[]>([]);
    const [permissions, setPermissions] = useState<DataSourcePermissionQueryResult[]>([]);

    useEffect(() => {
        if (!open) return;

        const fetchPermissions = async () => {
            try {
                const {data} = await axios.get<DataSourcePermissionQueryResult[]>(
                    `/documents/by-collection/${dataSourceId}/permissions`
                );
                setPermissions(data);
            } catch (error) {
                console.error('Error fetching permissions:', error);
                showSnackbar(parseError(error), "error");
            }
        };

        void fetchPermissions();
    }, [open, dataSourceId]);

    useEffect(() => {
        if (!open) return;

        const searchGroups = async () => {
            try {
                const data = await groupsService.fetchGroups(0, 10);
                setSearchGroupResults(data.content);
            } catch (error) {
                console.error('Error searching groups:', error);
                showSnackbar(parseError(error), "error");
            }
        };

        void searchGroups();
    }, [open, dataSourceId]);

    useEffect(() => {
        if (open) {
            setDataSourceOwner(owner);
        }
    }, [open, owner]);

    const handleClose = () => {
        setSearchQuery('')
        setSearchGroupResults([])
        setPermissions([])
        setDataSourceOwner('')
        onClose();
    };


    const searchGroups = async (query: string) => {
        try {
            const data = await groupsService.fetchGroups(0, 10, query);
            setSearchGroupResults(data.content);
        } catch (error) {
            console.error('Error searching groups:', error);
            showSnackbar(parseError(error), "error");
        }
    };

    const handlePermissionChange = (groupId: number, newPermission: 'READ_ONLY' | 'EDIT') => {
        setPermissions(prevPermissions => {
            return prevPermissions.map(p =>
                p.groupId === groupId ? {...p, permission: newPermission} : p
            );
        });
    };

    const handleSave = async () => {
        try {
            await axios.patch(
                `/documents/by-collection/${dataSourceId}/permissions`,
                permissions.map(permission => (
                    {
                        groupId: permission.groupId,
                        permissionType: permission.permission
                    }
                ))
            );
            showSnackbar(t('dataSourcePermissionDialog.permissionsUpdated'), 'success');
            onClose();
        } catch (error) {
            showSnackbar(parseError(error), "error");
            console.error('Error saving permissions:', error);
        }
    };

    function addNewPermission(group: GroupQueryResult) {
        setPermissions(prev => [
            ...prev,
            {groupId: group.id, name: group.name, permission: 'READ_ONLY'}
        ]);
    }

    return (
        <StyledDialog open={open} onClose={handleClose} aria-labelledby="permissions-dialog-title">
            <h3 id="permissions-dialog-title">{t('dataSourcePermissionDialog.addGroup')}</h3>

            <SearchInput
                value={searchQuery}
                onChange={(e) => {
                    setSearchQuery(e.target.value);
                    searchGroups(e.target.value);
                }}
                placeholder={t('dataSourcePermissionDialog.searchGroup')}
                inputProps={{
                    role: 'searchbox',
                    'aria-label': t('dataSourcePermissionDialog.searchGroup') as string,
                }}
                endAdornment={
                    <InputAdornment position="end">
                        <FieldHelpSuffix
                            title={t('dataSourcePermissionDialog.help.searchGroup')}
                            ariaLabel={t('dataSourcePermissionDialog.help.searchGroup') as string}
                        />
                    </InputAdornment>
                }
            />
            <GroupList>
                {searchGroupResults.length > 0 ? (
                    searchGroupResults.map(group => (
                        <GroupItem
                            key={group.id}
                            onClick={() => {
                                if (!permissions.some(g => g.groupId === group.id)) {
                                    addNewPermission(group);
                                    setSearchGroupResults(state => state.filter(prevGroup => prevGroup.id !== group.id))
                                }
                            }}
                        >
                            {group.name}
                        </GroupItem>
                    ))
                ) : (
                    <EmptyGroupItem>{t('groupList.emptyState')}</EmptyGroupItem>
                )}
            </GroupList>

            <h3>{t('dataSourcePermissionDialog.permissions')}</h3>

            <StyledTableWrapper>
                <StyledTable role="table" aria-label={t('dataSourcePermissionDialog.permissions') as string}>
                    <thead>
                    <tr>
                        <StyledTableCell>{t('dataSourcePermissionDialog.owner')}</StyledTableCell>
                        <StyledTableCell>{t('dataSourcePermissionDialog.access')}</StyledTableCell>
                        <StyledTableCell>{t('dataSourcePermissionDialog.actions')}</StyledTableCell>
                    </tr>
                    </thead>
                    <tbody>
                    <StyledTableRow key="owner">
                        <StyledTableCell>{dataSourceOwner || t('dataSourcePermissionDialog.system')}</StyledTableCell>
                        <StyledTableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <StyledSelect
                                    native
                                    value={'EDIT'}
                                    variant={'filled'}
                                    disabled={true}
                                    inputProps={{
                                        'aria-label': t('dataSourcePermissionDialog.access') as string,
                                    }}
                                >
                                    <option value="EDIT">{t('dataSourcePermissionDialog.edit')}</option>
                                </StyledSelect>
                                <FieldHelpSuffix
                                    title={t('dataSourcePermissionDialog.help.permission')}
                                    ariaLabel={t('dataSourcePermissionDialog.help.permission') as string}
                                />
                            </Box>
                        </StyledTableCell>
                        <StyledTableCell />
                    </StyledTableRow>

                    {permissions.map(permission => (
                        <StyledTableRow key={permission.groupId}>
                            <StyledTableCell>{permission.name}</StyledTableCell>
                            <StyledTableCell>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <StyledSelect
                                    native
                                    value={permissions.find(p => p.groupId === permission.groupId)?.permission || "READ_ONLY"}
                                    onChange={(e) => handlePermissionChange(permission.groupId, e.target.value as "READ_ONLY" | "EDIT")}
                                    variant={'outlined'}
                                    inputProps={{
                                        'aria-label': t('dataSourcePermissionDialog.access') as string,
                                    }}
                                >
                                    <option value="READ_ONLY">{t('dataSourcePermissionDialog.readOnly')}</option>
                                    <option value="EDIT">{t('dataSourcePermissionDialog.edit')}</option>
                                </StyledSelect>
                                <FieldHelpSuffix
                                    title={t('dataSourcePermissionDialog.help.permission')}
                                    ariaLabel={t('dataSourcePermissionDialog.help.permission') as string}
                                />
                                </Box>
                            </StyledTableCell>
                            <StyledTableCell>
                                <Button variant="outlined" color="primary" onClick={() => {
                                    setPermissions(prev => prev.filter(p => p.groupId !== permission.groupId))
                                    if (!searchGroupResults.some(g => g.id === permission.groupId)) {
                                        setSearchGroupResults(prev => [
                                            ...prev,
                                            {id: permission.groupId, name: permission.name}
                                        ])
                                    }
                                }
                                }>
                                    {t('dataSourcePermissionDialog.remove')}
                                </Button>
                            </StyledTableCell>
                        </StyledTableRow>
                    ))}
                    </tbody>
                </StyledTable>
            </StyledTableWrapper>
            <ActionsContainer>
                <CancelButton onClick={handleClose}>{t('dataSourcePermissionDialog.cancel')}</CancelButton>
                <ActionButton onClick={handleSave}>{t('dataSourcePermissionDialog.save')}</ActionButton>
            </ActionsContainer>
        </StyledDialog>
    );
};

export default DataSourcePermissionDialog;
